import React, { useState } from 'react';
import { 
  Brain, Download, Play, ChevronRight, ChevronDown,
  Lightbulb, Target, Zap, BookOpen, ExternalLink,
  Clock, Users, Star, CheckCircle, ArrowRight
} from 'lucide-react';
import { AnalysisMethodology, MethodologyStep } from '../../types/enhancedAnalysis';

interface MethodologyShowcaseProps {
  methodology: AnalysisMethodology;
  onDownloadTemplate: () => void;
  onTryAnalysis: () => void;
}

interface StepCardProps {
  step: MethodologyStep;
  isActive: boolean;
  isCompleted: boolean;
  onClick: () => void;
}

const StepCard: React.FC<StepCardProps> = ({ step, isActive, isCompleted, onClick }) => {
  return (
    <div
      onClick={onClick}
      className={`cursor-pointer transition-all duration-300 ${
        isActive 
          ? 'bg-blue-50 border-blue-300 shadow-md' 
          : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
      } border-2 rounded-xl p-6`}
    >
      <div className="flex items-start space-x-4">
        {/* 步骤编号 */}
        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
          isCompleted 
            ? 'bg-green-500 text-white' 
            : isActive 
              ? 'bg-blue-500 text-white' 
              : 'bg-gray-200 text-gray-600'
        }`}>
          {isCompleted ? <CheckCircle className="w-5 h-5" /> : step.order}
        </div>

        <div className="flex-1 min-w-0">
          <h4 className={`font-semibold mb-2 ${isActive ? 'text-blue-900' : 'text-gray-900'}`}>
            {step.title}
          </h4>
          <p className="text-gray-600 text-sm mb-3 line-clamp-2">
            {step.description}
          </p>
          
          {/* 示例 */}
          {step.example && (
            <div className="bg-gray-50 rounded-lg p-3 mb-3">
              <div className="text-xs text-gray-500 mb-1">示例：</div>
              <div className="text-sm text-gray-700 italic">"{step.example}"</div>
            </div>
          )}

          {/* 技巧标签 */}
          {step.tips.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {step.tips.slice(0, 2).map((tip, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full"
                >
                  <Lightbulb className="w-3 h-3 mr-1" />
                  {tip}
                </span>
              ))}
              {step.tips.length > 2 && (
                <span className="text-xs text-gray-500">+{step.tips.length - 2} 更多</span>
              )}
            </div>
          )}
        </div>

        <ChevronRight className={`w-5 h-5 transition-transform ${
          isActive ? 'rotate-90 text-blue-500' : 'text-gray-400'
        }`} />
      </div>
    </div>
  );
};

const MethodologyShowcase: React.FC<MethodologyShowcaseProps> = ({
  methodology,
  onDownloadTemplate,
  onTryAnalysis
}) => {
  const [activeStep, setActiveStep] = useState<string>(methodology.steps[0]?.id || '');
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  const [showAllSteps, setShowAllSteps] = useState(false);

  const currentStep = methodology.steps.find(step => step.id === activeStep);
  const visibleSteps = showAllSteps ? methodology.steps : methodology.steps.slice(0, 3);

  const markStepCompleted = (stepId: string) => {
    setCompletedSteps(prev => new Set([...prev, stepId]));
    
    // 自动移动到下一步
    const currentIndex = methodology.steps.findIndex(step => step.id === stepId);
    if (currentIndex < methodology.steps.length - 1) {
      setActiveStep(methodology.steps[currentIndex + 1].id);
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-600 bg-green-100';
      case 'intermediate': return 'text-yellow-600 bg-yellow-100';
      case 'advanced': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 rounded-2xl p-8 border border-purple-200">
      {/* 头部 */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-600 rounded-2xl mb-4">
          <Brain className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">分析方法论</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          了解我们如何运用 <span className="font-semibold text-purple-600">{methodology.name}</span> 
          进行深度分析，掌握专业的思维框架
        </p>
      </div>

      {/* 方法论概览 */}
      <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Target className="w-6 h-6 text-purple-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">{methodology.framework}</div>
            <div className="text-sm text-gray-500">分析框架</div>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">{methodology.estimatedTime}</div>
            <div className="text-sm text-gray-500">预计时间(分钟)</div>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Zap className="w-6 h-6 text-green-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">{methodology.steps.length}</div>
            <div className="text-sm text-gray-500">分析步骤</div>
          </div>
          
          <div className="text-center">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3 ${getDifficultyColor(methodology.difficulty)}`}>
              <Star className="w-6 h-6" />
            </div>
            <div className="text-2xl font-bold text-gray-900 capitalize">{methodology.difficulty}</div>
            <div className="text-sm text-gray-500">难度等级</div>
          </div>
        </div>
      </div>

      {/* 分析步骤 */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">分析步骤</h3>
          {methodology.steps.length > 3 && (
            <button
              onClick={() => setShowAllSteps(!showAllSteps)}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors"
            >
              <span className="text-sm font-medium">
                {showAllSteps ? '收起' : `查看全部 ${methodology.steps.length} 步`}
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showAllSteps ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {visibleSteps.map((step) => (
            <StepCard
              key={step.id}
              step={step}
              isActive={activeStep === step.id}
              isCompleted={completedSteps.has(step.id)}
              onClick={() => setActiveStep(step.id)}
            />
          ))}
        </div>

        {/* 当前步骤详情 */}
        {currentStep && (
          <div className="bg-white rounded-xl p-6 border-2 border-blue-200 shadow-sm">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  步骤 {currentStep.order}: {currentStep.title}
                </h4>
                <p className="text-gray-700 leading-relaxed">{currentStep.description}</p>
              </div>
              <button
                onClick={() => markStepCompleted(currentStep.id)}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">完成</span>
              </button>
            </div>

            {/* 详细示例 */}
            {currentStep.example && (
              <div className="bg-blue-50 rounded-lg p-4 mb-4">
                <h5 className="font-medium text-blue-900 mb-2">实际应用示例</h5>
                <p className="text-blue-800 italic">"{currentStep.example}"</p>
              </div>
            )}

            {/* 实用技巧 */}
            {currentStep.tips.length > 0 && (
              <div className="bg-yellow-50 rounded-lg p-4">
                <h5 className="font-medium text-yellow-900 mb-3 flex items-center">
                  <Lightbulb className="w-4 h-4 mr-2" />
                  实用技巧
                </h5>
                <ul className="space-y-2">
                  {currentStep.tips.map((tip, index) => (
                    <li key={index} className="flex items-start space-x-2 text-yellow-800">
                      <div className="w-1.5 h-1.5 bg-yellow-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-sm">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 相关资源 */}
      {methodology.relatedResources.length > 0 && (
        <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <BookOpen className="w-5 h-5 mr-2 text-blue-600" />
            相关学习资源
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {methodology.relatedResources.map((resource) => (
              <a
                key={resource.id}
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
              >
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <ExternalLink className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                    {resource.title}
                  </div>
                  <div className="text-sm text-gray-500 truncate">{resource.description}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* 敬请期待区域 */}
      <div className="text-center py-12 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl border-2 border-dashed border-blue-200">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lightbulb className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            分析方法详解
          </h3>
          <p className="text-gray-600 mb-6 leading-relaxed">
            我们正在为您准备更详细的分析方法教程，包括步骤指导、实战模板和互动练习。
          </p>
          <div className="inline-flex items-center px-6 py-3 bg-white border border-gray-200 rounded-lg text-gray-500">
            <Clock className="w-4 h-4 mr-2" />
            敬请期待
          </div>
        </div>
      </div>

      {/* 进度指示器 */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
          <span>学习进度</span>
          <span>{completedSteps.size} / {methodology.steps.length} 步骤完成</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all duration-500"
            style={{ width: `${(completedSteps.size / methodology.steps.length) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default MethodologyShowcase;